import type { CardIdentity, ChecklistEntry, ChecklistParallelDefinition } from '../shared/types';
import {
  isChecklistPlaceholderLabel,
  decideChecklistParallelApplicability,
  decideChecklistPlainVersion,
  composeChecklistSelectionIdentity,
  type ChecklistSelectionPolicy,
  type ChecklistSourceSelection,
  type CardRecord,
  type ComposeChecklistSelectionResult,
} from '@tcvs/shared';

const exact = (value: unknown) => (typeof value === 'string' ? value.trim() : '');
const record = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);
/** Validate advisory shape and its exact row association; never a save authority token. */
export function checklistEntryPolicy(entry: ChecklistEntry): ChecklistSelectionPolicy | undefined {
  if (
    !record(entry) ||
    !record(entry.identity) ||
    !record(entry.key) ||
    !record(entry.source) ||
    !exact(entry.identity.number) ||
    !['category', 'set', 'number', 'name'].every(
      (field) => typeof (entry.identity as unknown as Record<string, unknown>)[field] === 'string',
    ) ||
    ![
      'sourceSystem',
      'sourceTargetId',
      'sourceItemId',
      'sourceSubsetId',
      'snapshotFingerprint',
      'sourceFamilyId',
    ].every((field) => exact((entry.source as unknown as Record<string, unknown>)[field]))
  )
    return undefined;
  const policy = entry.selectionPolicy;
  const source = entry.source;
  if (
    !record(policy) ||
    !source ||
    !record(policy.plainVersion) ||
    !['PLAIN', 'INTRINSIC'].includes(policy.rowTreatmentStatus) ||
    !Array.isArray(policy.correctedIdentityFields) ||
    !policy.correctedIdentityFields.every((field) => typeof field === 'string') ||
    !record(policy.subsetDefinition)
  )
    return undefined;
  const identityFields = [
    'category',
    'set',
    'number',
    'name',
    'year',
    'brand',
    'subset',
    'attributes',
    'rookie',
    'autograph',
    'memorabilia',
    'serialNumbered',
    'team',
    'language',
    'variation',
    'serialTo',
    'serialNumber',
  ];
  if (
    policy.correctedIdentityFields.some((field) => !identityFields.includes(field)) ||
    (policy.rowVariationIsGeneratedAlias !== undefined &&
      policy.rowVariationIsGeneratedAlias !== true) ||
    (policy.revision !== undefined && typeof policy.revision !== 'string') ||
    (policy.plainVersion.reason !== undefined && typeof policy.plainVersion.reason !== 'string')
  )
    return undefined;
  const subset = policy.subsetDefinition;
  if (
    !record(subset.source) ||
    !exact(subset.name) ||
    !exact(subset.source.subsetId) ||
    !exact(subset.source.snapshotFingerprint) ||
    exact(subset.name) !== checklistRosterSubset(entry) ||
    exact(subset.source.system) !== exact(source.sourceSystem) ||
    exact(subset.source.targetId) !== exact(source.sourceTargetId) ||
    !exact(subset.source.familyId) ||
    !exact(source.sourceFamilyId) ||
    exact(subset.source.familyId) !== exact(source.sourceFamilyId) ||
    (subset.noPlainVersion !== undefined && typeof subset.noPlainVersion !== 'boolean')
  )
    return undefined;
  if (
    policy.rowTreatmentStatus === 'INTRINSIC' &&
    (!record(policy.intrinsicTreatment) ||
      !exact(policy.intrinsicTreatment.definitionId) ||
      !exact(policy.intrinsicTreatment.name) ||
      source.sourceDefinitionType !== 'PARALLEL')
  )
    return undefined;
  if (
    policy.rowTreatmentStatus === 'PLAIN' &&
    (policy.intrinsicTreatment !== undefined ||
      source.sourceDefinitionType !== 'SUBSET' ||
      exact(subset.source.subsetId) !== exact(source.sourceSubsetId))
  )
    return undefined;
  const plain = decideChecklistPlainVersion({
    subsetDefinition: subset,
    policyComplete: true,
    ...(policy.intrinsicTreatment === undefined
      ? {}
      : { intrinsicTreatment: policy.intrinsicTreatment }),
  });
  if (plain.decision !== policy.plainVersion.decision) return undefined;
  return policy;
}
export function checklistPlainChoiceAllowed(entry: ChecklistEntry | undefined): boolean {
  const policy = entry && checklistEntryPolicy(entry);
  return policy?.rowTreatmentStatus === 'PLAIN' && policy.plainVersion.decision === 'allowed';
}
export function checklistRowContext(entry: ChecklistEntry) {
  return {
    sourceSystem: exact(entry.source?.sourceSystem),
    sourceTargetId: exact(entry.source?.sourceTargetId),
    subset: checklistRosterSubset(entry),
    number: exact(entry.identity.number),
    familyId: entry.source?.sourceFamilyId,
    rosterPresent: true,
  };
}
function canonical(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  if (record(value))
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${canonical(value[key])}`)
      .join(',')}}`;
  return JSON.stringify(value);
}
export function samePhysicalChecklistSelection(
  card: Pick<CardRecord, 'identity' | 'identityReviewDraft' | 'checklistSourceLineage'>,
  selection: ChecklistSourceSelection,
): boolean {
  // A draft is the working context even when its source is absent: never fall back to old Card lineage.
  const existing =
    card.identityReviewDraft !== undefined
      ? card.identityReviewDraft.checklistSourceSelection
      : card.checklistSourceLineage &&
          ['DRAFT', 'VERIFIED'].includes(card.checklistSourceLineage.status)
        ? card.checklistSourceLineage
        : undefined;
  if (!existing) return false;
  for (const field of [
    'checklistId',
    'sourceSystem',
    'sourceTargetId',
    'sourceItemId',
    'sourceSubsetId',
    'sourceDefinitionType',
    'sourceFamilyId',
    'snapshotFingerprint',
    'subset',
  ] as const) {
    if (!exact(existing[field]) || exact(existing[field]) !== exact(selection[field])) return false;
  }
  return (
    canonical(existing.naturalKey) === canonical(selection.naturalKey) &&
    canonical(existing.parallel) === canonical(selection.parallel)
  );
}
export function checklistSelectionSource(
  checklistId: string,
  entry: ChecklistEntry,
  parallel?: ChecklistParallelDefinition,
): ChecklistSourceSelection | undefined {
  if (!entry.source) return undefined;
  return {
    checklistId,
    ...entry.source,
    naturalKey: entry.key,
    subset: checklistRosterSubset(entry),
    ...(parallel
      ? {
          parallel: {
            definitionId: parallel.definitionId,
            name: parallel.name,
            sourceSubsetId: parallel.source.subsetId,
            snapshotFingerprint: parallel.source.snapshotFingerprint,
          },
        }
      : {}),
  };
}
function validParallel(definition: ChecklistParallelDefinition): boolean {
  if (
    !record(definition) ||
    !record(definition.source) ||
    !record(definition.applicability) ||
    !exact(definition.definitionId) ||
    !exact(definition.name) ||
    !['confirmed', 'source-claimed', 'unknown'].includes(definition.applicabilityConfidence) ||
    !['all-cards-in-subset', 'explicit-cards', 'unknown'].includes(definition.applicability.kind)
  )
    return false;
  return (
    definition.applicability.kind !== 'explicit-cards' ||
    (Array.isArray(definition.applicability.numbers) &&
      definition.applicability.numbers.every((number) => typeof number === 'string'))
  );
}
/** Detail's deliberate operator review may select a qualified, visibly unverified subset inference. */
export interface ChecklistSelectionOptions {
  allowUnverifiedSubsetSelection?: boolean;
}
function qualifiedUnverifiedSubsetSelection(
  entry: ChecklistEntry,
  definition: ChecklistParallelDefinition,
): boolean {
  const source = entry.source;
  const subset = entry.selectionPolicy?.subsetDefinition;
  if (!source || !subset) return false;
  return (
    definition.applicabilityConfidence === 'unknown' &&
    definition.applicability.kind === 'all-cards-in-subset' &&
    exact(definition.applicability.subset) === checklistRosterSubset(entry) &&
    !!exact(definition.source.subsetId) &&
    exact(definition.source.system) === exact(source.sourceSystem) &&
    exact(definition.source.targetId) === exact(source.sourceTargetId) &&
    !!exact(source.sourceFamilyId) &&
    exact(definition.source.familyId) === exact(source.sourceFamilyId) &&
    exact(subset.source.familyId) === exact(source.sourceFamilyId) &&
    !!exact(source.snapshotFingerprint) &&
    exact(definition.source.snapshotFingerprint) === exact(source.snapshotFingerprint) &&
    exact(subset.source.snapshotFingerprint) === exact(source.snapshotFingerprint)
  );
}
export type ChecklistIdentityProposal =
  | ComposeChecklistSelectionResult
  | {
      ok: false;
      reason: 'SOURCE_POLICY_UNAVAILABLE' | 'PARALLEL_REQUIRED' | 'PARALLEL_NOT_APPLICABLE';
      fields: (keyof CardIdentity)[];
    };
export function composeReviewedChecklistIdentity(
  entry: ChecklistEntry,
  definition?: ChecklistParallelDefinition,
  observed?: {
    card: Pick<CardRecord, 'identity' | 'identityReviewDraft' | 'checklistSourceLineage'>;
    checklistId: string;
  },
  options: ChecklistSelectionOptions = {},
): ChecklistIdentityProposal {
  const policy = checklistEntryPolicy(entry);
  if (!policy || entry.unresolvedClassification)
    return { ok: false, reason: 'SOURCE_POLICY_UNAVAILABLE', fields: [] };
  if (
    !definition &&
    policy.rowTreatmentStatus === 'PLAIN' &&
    policy.plainVersion.decision !== 'allowed'
  )
    return { ok: false, reason: 'PARALLEL_REQUIRED', fields: ['variation'] };
  if (definition) {
    if (!validParallel(definition))
      return { ok: false, reason: 'PARALLEL_NOT_APPLICABLE', fields: ['variation'] };
    const applicability = decideChecklistParallelApplicability(
      checklistRowContext(entry),
      definition,
    );
    if (
      applicability.decision !== 'allowed' &&
      !(
        options.allowUnverifiedSubsetSelection === true &&
        applicability.decision === 'unknown' &&
        applicability.reason === 'APPLICABILITY_UNKNOWN' &&
        qualifiedUnverifiedSubsetSelection(entry, definition)
      )
    )
      return { ok: false, reason: 'PARALLEL_NOT_APPLICABLE', fields: ['variation'] };
  }
  const row = { ...entry.identity, subset: checklistRosterSubset(entry) };
  if (
    policy.rowVariationIsGeneratedAlias === true &&
    policy.rowTreatmentStatus === 'PLAIN' &&
    !policy.correctedIdentityFields.includes('variation') &&
    exact(row.variation) === exact(row.subset)
  )
    delete row.variation;
  const selection = observed && checklistSelectionSource(observed.checklistId, entry, definition);
  const identity = observed?.card.identityReviewDraft?.identity ?? observed?.card.identity;
  return composeChecklistSelectionIdentity({
    rowIdentity: row,
    rowTreatmentStatus: policy.rowTreatmentStatus,
    correctedFields: policy.correctedIdentityFields,
    ...(policy.intrinsicTreatment ? { rowTreatment: policy.intrinsicTreatment } : {}),
    ...(definition ? { parallel: definition } : {}),
    ...(identity && selection && observed
      ? {
          observed: {
            identity,
            sameQualifiedRowAndTreatment: samePhysicalChecklistSelection(observed.card, selection),
          },
        }
      : {}),
  });
}

export const NO_PARALLEL_DEFINITION_ID = '__none__';

export interface ChecklistSubsetChoice {
  entry: ChecklistEntry;
  index: number;
}

function fold(value: string | undefined): string {
  return (value ?? '').trim().toLocaleLowerCase('en-US');
}

export function checklistRosterSubset(entry: ChecklistEntry): string {
  return (
    entry.identity.subset?.trim() ||
    entry.key.cardSet?.trim() ||
    entry.identity.variation?.trim() ||
    ''
  );
}

function checklistCandidateFamilyKey(entry: ChecklistEntry, index: number): string {
  const familyId = entry.source?.sourceFamilyId?.trim();
  if (familyId === undefined || familyId === '') return `entry:${index}`;
  return [
    entry.source?.sourceSystem ?? '',
    entry.source?.sourceTargetId ?? '',
    familyId,
    exact(entry.identity.number),
    exact(entry.identity.name),
    exact(checklistRosterSubset(entry)),
  ].join('|');
}

function checklistSubsetRepresentativeRank(entry: ChecklistEntry): number {
  if (entry.source?.sourceDefinitionType === 'SUBSET') return 0;
  const subset = exact(checklistRosterSubset(entry));
  const variation = fold(entry.identity.variation);
  return variation === '' || variation === 'base' || variation === subset ? 1 : 2;
}

/**
 * Collapse v7 row-level parallel records into one visible subset choice while
 * retaining the original candidate indexes for exact source-lineage selection.
 */
export function checklistSubsetChoices(
  candidates: readonly ChecklistEntry[],
): ChecklistSubsetChoice[] {
  const grouped = new Map<string, ChecklistSubsetChoice>();
  candidates.forEach((entry, index) => {
    const key = checklistCandidateFamilyKey(entry, index);
    const current = grouped.get(key);
    if (
      current === undefined ||
      checklistSubsetRepresentativeRank(entry) < checklistSubsetRepresentativeRank(current.entry)
    ) {
      grouped.set(key, { entry, index });
    }
  });
  return [...grouped.values()];
}

/** Map any row-level roster record back to its visible subset representative. */
export function checklistSubsetChoiceForEntry(
  candidates: readonly ChecklistEntry[],
  selected: ChecklistEntry,
): ChecklistSubsetChoice | undefined {
  const selectedIndex = candidates.indexOf(selected);
  if (selectedIndex < 0) return undefined;
  const selectedFamilyKey = checklistCandidateFamilyKey(selected, selectedIndex);
  return checklistSubsetChoices(candidates).find(
    ({ entry, index }) => checklistCandidateFamilyKey(entry, index) === selectedFamilyKey,
  );
}

/** Resolve a visible subset choice back to the exact v7 parallel roster row. */
export function checklistEntryForParallelChoice(
  selected: ChecklistEntry | undefined,
  candidates: readonly ChecklistEntry[],
  definition: ChecklistParallelDefinition | undefined,
): ChecklistEntry | undefined {
  if (selected === undefined || definition === undefined) return selected;
  const selectedIndex = candidates.indexOf(selected);
  if (selectedIndex < 0 || selected.source?.sourceFamilyId === undefined) return selected;
  const familyKey = checklistCandidateFamilyKey(selected, selectedIndex);
  const familyCandidates = candidates.filter(
    (candidate, index) => checklistCandidateFamilyKey(candidate, index) === familyKey,
  );
  return (
    familyCandidates.find(
      (candidate) =>
        candidate.source?.sourceDefinitionType === 'PARALLEL' &&
        candidate.source.sourceSubsetId === definition.source.subsetId &&
        candidate.selectionPolicy?.intrinsicTreatment?.definitionId === definition.definitionId,
    ) ?? selected
  );
}

export function applicableParallelDefinitions(
  entry: ChecklistEntry,
  definitions: readonly ChecklistParallelDefinition[],
  options: ChecklistSelectionOptions = {},
): ChecklistParallelDefinition[] {
  return definitions
    .filter(
      (definition) =>
        !isChecklistPlaceholderLabel(definition.name) &&
        composeReviewedChecklistIdentity(entry, definition, undefined, options).ok,
    )
    .sort((a, b) => a.name.localeCompare(b.name) || a.definitionId.localeCompare(b.definitionId));
}

export function reviewedIdentityFromChecklist(
  entry: ChecklistEntry,
  definition?: ChecklistParallelDefinition,
  observed?: {
    card: Pick<CardRecord, 'identity' | 'identityReviewDraft' | 'checklistSourceLineage'>;
    checklistId: string;
  },
  options: ChecklistSelectionOptions = {},
): CardIdentity | null {
  const result = composeReviewedChecklistIdentity(entry, definition, observed, options);
  return result.ok ? result.identity : null;
}
/** No roster means advisory/manual context only; never fabricate a source-backed identity. */
export function reviewedIdentityFromSubsetFallback(
  _identity: CardIdentity,
  _subset: string,
  _definition?: ChecklistParallelDefinition,
): CardIdentity | null {
  return null;
}

export function checklistCandidateLabel(
  entry: ChecklistEntry,
  candidates: readonly ChecklistEntry[],
): string {
  const subset = checklistRosterSubset(entry);
  const names = new Set(candidates.map((candidate) => fold(candidate.identity.name)));
  return names.size > 1
    ? `${subset} · #${entry.identity.number || entry.key.number} ${entry.identity.name}`
    : subset;
}

/** Explain known composition conflicts without treating unavailable applicability as permission. */
export function checklistSelectionConflictMessages(
  entry: ChecklistEntry | undefined,
  definitions: readonly ChecklistParallelDefinition[],
  options: ChecklistSelectionOptions = {},
): string[] {
  if (!entry || !checklistEntryPolicy(entry)) return [];
  return definitions.flatMap((definition) => {
    const result = composeReviewedChecklistIdentity(entry, definition, undefined, options);
    if (result.ok) return [];
    if (result.reason === 'SERIAL_TO_CONFLICT')
      return [
        `${definition.name} is unavailable: the roster print run (/${entry.identity.serialTo}) conflicts with the treatment print run (/${definition.serialTo}). Retry source options or use explicit manual review.`,
      ];
    if (result.reason === 'INVALID_SERIAL_TO')
      return [
        `${definition.name} is unavailable because its roster or treatment print run is invalid. Retry source options or use explicit manual review.`,
      ];
    if (result.reason === 'ROW_VARIATION_CONFLICT')
      return [
        `${definition.name} conflicts with the roster's existing treatment. Review the exact source or use explicit manual review.`,
      ];
    if (result.reason === 'SECOND_TREATMENT')
      return [
        `${definition.name} cannot be added to a card that already has its intrinsic treatment.`,
      ];
    return [];
  });
}

export function parallelSelectionOptions(
  definitions: readonly ChecklistParallelDefinition[],
  entry?: ChecklistEntry,
  options: ChecklistSelectionOptions = {},
): Array<{ value: string; label: string }> {
  const policy = entry && checklistEntryPolicy(entry);
  const eligible = entry ? applicableParallelDefinitions(entry, definitions, options) : [];
  return [
    ...(checklistPlainChoiceAllowed(entry)
      ? [{ value: NO_PARALLEL_DEFINITION_ID, label: 'No parallel / Base version' }]
      : []),
    ...eligible.map((definition) => {
      const composed =
        entry && composeReviewedChecklistIdentity(entry, definition, undefined, options);
      const displayDefinition =
        policy?.rowTreatmentStatus === 'INTRINSIC' && composed?.ok
          ? {
              ...definition,
              serialTo: composed.identity.serialTo,
              autograph: composed.identity.autograph ?? false,
              memorabilia: composed.identity.memorabilia ?? false,
            }
          : definition;
      return {
        value: definition.definitionId,
        label: `${parallelDefinitionLabel(displayDefinition)}${policy?.rowTreatmentStatus === 'INTRINSIC' ? ' · Intrinsic treatment' : ''}`,
      };
    }),
  ];
}

export function parallelDefinitionLabel(definition: ChecklistParallelDefinition): string {
  const details = [
    definition.serialTo === undefined
      ? definition.printRunText
      : definition.serialTo === 1
        ? '1/1'
        : `/${definition.serialTo}`,
    definition.autograph ? 'Autograph' : undefined,
    definition.memorabilia ? 'Memorabilia' : undefined,
    definition.shortPrint ? 'Short print' : undefined,
    definition.applicabilityConfidence === 'unknown' ? 'Applicability unverified' : undefined,
  ].filter((value): value is string => value !== undefined && value !== '');
  return details.length === 0 ? definition.name : `${definition.name} · ${details.join(' · ')}`;
}
